import { useState } from 'react'
import PropTypes from 'prop-types'

import Auth from '../../../ui/modules/auth'

import {
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Link,
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'

import { ReactComponent as Eye } from '../../../assets/icons/eye.svg'
import { ReactComponent as EyeClosed } from '../../../assets/icons/eye-closed.svg'
import { ReactComponent as ArrowRight } from '../../../assets/icons/arrow-right.svg'

import { makeFormPropTypes } from '../../../common/formikPropTypes'
import { resetPasswordPropTypes } from '../../../common/propTypes'
import useTranslations from '../../../i18n/useTranslations'
import { COGNITO_ERRORS_MAP } from '../../../auth/cognito/errors'

import styles from './styles'

const ResetPassword = ({
  values,
  fieldErrors,
  formErrors,
  touched,
  onChange,
  onBlur,
  onSubmit,
  onResendCode,
  isSendingRequest,
}) => {
  const t = useTranslations()

  const [showPassword, setShowPassword] = useState(false)

  const infoAlert = {
    type: 'warning',
    text: t('reset-password:warning:validate-password'),
  }
  const getErrorAlert = error => ({
    type: 'error',
    text: t(COGNITO_ERRORS_MAP[error] ?? 'error:general'),
  })

  const alertContent =
    !formErrors || !formErrors.length ? infoAlert : getErrorAlert(formErrors[0])

  const isSubmitBtnDisabled =
    formErrors?.length !== 0 || Object.entries(fieldErrors)?.length !== 0

  return (
    <Auth title={t('login:title')} onSubmit={onSubmit}>
      <Grid item>
        <Typography variant="body1" sx={styles.explanation}>
          {t('reset-password:enter-code-and-choose-password')}
        </Typography>
        <TextField
          id="code"
          label={t('noun:code')}
          autoComplete="code"
          error={Boolean(touched.code && fieldErrors.code)}
          helperText={
            touched.code && fieldErrors.code ? t(fieldErrors.code) : ' '
          }
          value={values.code}
          onChange={onChange}
          onBlur={onBlur}
          className="input-full"
        />
      </Grid>
      <Grid item>
        <Link component="button" onClick={onResendCode} sx={styles.resendCode}>
          {t('reset-password:email-not-received-send-again')}
        </Link>
        <TextField
          label={t('noun:my-new-password')}
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          error={Boolean(touched.password && fieldErrors.password)}
          helperText={
            touched.password && fieldErrors.password
              ? t(fieldErrors.password)
              : ' '
          }
          value={values.password}
          onChange={onChange}
          onBlur={onBlur}
          InputProps={{
            endAdornment: (
              <InputAdornment
                onClick={() => setShowPassword(showPassword => !showPassword)}
                position="end"
              >
                <IconButton
                  aria-label="toggle password visibility"
                  size="large"
                >
                  {showPassword ? <Eye /> : <EyeClosed />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          className="input-full"
        />
        <Alert severity={alertContent.type} sx={styles.alert}>
          <Typography variant="body2">{alertContent.text}</Typography>
        </Alert>
        <LoadingButton
          fullWidth
          type="submit"
          endIcon={<ArrowRight />}
          sx={styles.submitButton}
          loading={isSendingRequest}
          disabled={isSubmitBtnDisabled}
          loadingIndicator={
            <CircularProgress sx={styles.circularProgress} size={16} />
          }
        >
          {t('action:login')}
        </LoadingButton>
      </Grid>
    </Auth>
  )
}

ResetPassword.propTypes = {
  ...makeFormPropTypes(resetPasswordPropTypes),
  onResendCode: PropTypes.func,
  isSendingRequest: PropTypes.bool,
}

export default ResetPassword
